import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';

/**
 * Paso 4 — Calendario visual de días disponibles.
 *
 * Click en día disponible -> excluido-manual. Click otra vez -> disponible.
 * Los festivos NO se pasan como prop; el conteo real lo da el backend en el
 * paso 7 (preview-capacidad). Esto evita una llamada extra.
 */
export default function Paso4DiasDisponibles({ payload, dispatch }) {
  const { fecha_inicio, fecha_limite, excluir_fines_de_semana, permite_fin_semana, excluidos_manuales } = payload;

  const primerMes = useMemo(() => {
    if (!fecha_inicio) return null;
    const [y, m] = fecha_inicio.split('-').map(Number);
    return { year: y, month: m };
  }, [fecha_inicio]);

  const ultimoMes = useMemo(() => {
    if (!fecha_limite) return null;
    const [y, m] = fecha_limite.split('-').map(Number);
    return { year: y, month: m };
  }, [fecha_limite]);

  const [mesActual, setMesActual] = useState(primerMes);

  React.useEffect(() => {
    if (primerMes && (!mesActual ||
      mesActual.year * 12 + mesActual.month < primerMes.year * 12 + primerMes.month ||
      mesActual.year * 12 + mesActual.month > (ultimoMes?.year || 0) * 12 + (ultimoMes?.month || 0)
    )) {
      setMesActual(primerMes);
    }
  }, [fecha_inicio, fecha_limite]); // eslint-disable-line

  if (!fecha_inicio || !fecha_limite) {
    return (
      <div className="text-sm text-slate-500 max-w-2xl">
        Define la fecha de inicio y la fecha límite en el paso anterior para ver
        el calendario.
      </div>
    );
  }

  if (!mesActual) return null;

  const puedeIrAtras =
    primerMes &&
    (mesActual.year > primerMes.year ||
      (mesActual.year === primerMes.year && mesActual.month > primerMes.month));
  const puedeIrAdelante =
    ultimoMes &&
    (mesActual.year < ultimoMes.year ||
      (mesActual.year === ultimoMes.year && mesActual.month < ultimoMes.month));

  function navega(delta) {
    let m = mesActual.month + delta;
    let y = mesActual.year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMesActual({ year: y, month: m });
  }

  const excluidosSet = new Set(excluidos_manuales);

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-slate-500">
        Toca cualquier día disponible para excluirlo. Vuelve a tocarlo para
        reincluirlo. Los fines de semana y festivos siguen las exclusiones del
        paso anterior.
      </p>

      {/* Fase 19a · #17 — permitir reservas en fin de semana. El calendario se
          actualiza al instante porque excluir_fines_de_semana se deriva del toggle. */}
      <label className="flex items-center gap-2 py-2 px-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={permite_fin_semana ?? false}
          onChange={(e) => dispatch({
            type: 'TOGGLE_PERMITE_FIN_SEMANA',
            value: e.target.checked,
          })}
          className="rounded border-slate-300 text-primary focus:ring-primary"
        />
        <span className="text-sm text-slate-700">
          Permitir reservas en <strong>sábado y domingo</strong>
        </span>
      </label>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navega(-1)}
          disabled={!puedeIrAtras}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="font-medium text-slate-800 text-sm">
          {nombreMes(mesActual.month)} {mesActual.year}
        </div>

        <button
          type="button"
          onClick={() => navega(1)}
          disabled={!puedeIrAdelante}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes siguiente"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <Calendario
        year={mesActual.year}
        month={mesActual.month}
        fechaInicio={fecha_inicio}
        fechaLimite={fecha_limite}
        excluirFindes={excluir_fines_de_semana}
        excluidos={excluidosSet}
        onToggle={(fecha) => dispatch({ type: 'TOGGLE_DIA_EXCLUIDO', fecha })}
      />

      <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs text-slate-500">
        <div>
          {excluidos_manuales.length === 0
            ? 'Sin días excluidos manualmente.'
            : `${excluidos_manuales.length} día(s) excluido(s) manualmente.`}
        </div>
        {excluidos_manuales.length > 0 && (
          <button
            type="button"
            onClick={() => dispatch({ type: 'CLEAR_DIAS_EXCLUIDOS' })}
            className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Restaurar
          </button>
        )}
      </div>

      <Leyenda />
    </div>
  );
}

function Calendario({ year, month, fechaInicio, fechaLimite, excluirFindes, excluidos, onToggle }) {
  const primerDiaMes = new Date(Date.UTC(year, month - 1, 1));
  const ultimoDiaMes = new Date(Date.UTC(year, month, 0));
  const primerDow = (primerDiaMes.getUTCDay() + 6) % 7; // lunes=0
  const totalDias = ultimoDiaMes.getUTCDate();

  const celdas = [];
  for (let i = 0; i < primerDow; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(d);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 text-[10px] text-slate-500 uppercase tracking-wide">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-100">
        {celdas.map((dia, idx) => {
          if (dia === null) {
            return <div key={idx} className="bg-white aspect-square" />;
          }
          const fechaStr = `${year}-${String(month).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const props = clasificarDia({ fecha: fechaStr, fechaInicio, fechaLimite, excluirFindes, excluidos, year, month, dia });
          return (
            <DiaCelda
              key={fechaStr}
              dia={dia}
              clase={props.clase}
              titulo={props.titulo}
              clickable={props.clickable}
              onClick={() => props.clickable && onToggle(fechaStr)}
            />
          );
        })}
      </div>
    </div>
  );
}

function DiaCelda({ dia, clase, titulo, clickable, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      title={titulo}
      className={
        'aspect-square text-xs flex items-center justify-center transition-colors ' +
        clase +
        (clickable ? ' cursor-pointer' : ' cursor-default')
      }
    >
      {dia}
    </button>
  );
}

function clasificarDia({ fecha, fechaInicio, fechaLimite, excluirFindes, excluidos, year, month, dia }) {
  if (fecha < fechaInicio || fecha > fechaLimite) {
    return { clase: 'bg-slate-50 text-slate-300', clickable: false, titulo: 'Fuera del rango' };
  }
  const d = new Date(Date.UTC(year, month - 1, dia));
  const dow = d.getUTCDay();
  const esFinde = dow === 0 || dow === 6;
  const esExcluido = excluidos.has(fecha);

  if (esFinde && excluirFindes) {
    return {
      clase: 'bg-slate-50 text-slate-400 line-through',
      clickable: false,
      titulo: 'Fin de semana (excluido por configuración)',
    };
  }
  if (esExcluido) {
    return {
      clase: 'bg-red-50 text-red-600 line-through hover:bg-red-100',
      clickable: true,
      titulo: 'Excluido manualmente — click para reincluir',
    };
  }
  return {
    clase: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    clickable: true,
    titulo: 'Disponible — click para excluir',
  };
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function nombreMes(m) {
  return MESES[m - 1] || '';
}

function Leyenda() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-slate-500">
      <LeyendaItem clase="bg-emerald-50 text-emerald-700" label="Disponible" />
      <LeyendaItem clase="bg-red-50 text-red-600 line-through" label="Excluido manualmente" />
      <LeyendaItem clase="bg-slate-50 text-slate-400 line-through" label="Fin de semana / fuera de rango" />
    </div>
  );
}

function LeyendaItem({ clase, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-5 h-5 rounded text-center text-[10px] leading-5 ${clase}`}>·</span>
      <span>{label}</span>
    </div>
  );
}
